// Capture du viewport visible par reconstruction DOM (modern-screenshot).
// Exclut les noeuds marques [data-report-ui] (FAB, overlays de rapport).
//
// modern-screenshot echoue par intermittence sur les pages chargees d'images :
// son rendu interne retombe sur un canvas transparent, et comme la sortie est
// en JPEG (sans alpha) le transparent devient NOIR. On limite ca en (1) attendant
// polices + images avant la capture, (2) detectant une capture noire et en
// reessayant une fois, (3) renvoyant '' si ca echoue encore (capture abandonnee
// plutot qu'envoyee noire).

import { domToJpeg } from 'modern-screenshot';
import { compressImage } from '../lib/imageUtils';

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(blob);
  });
}

function nextFrames() {
  return new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}

// Attend que les polices et les images deja presentes finissent de charger,
// avec un garde-fou pour ne jamais bloquer plus de timeoutMs.
async function waitForResources(timeoutMs = 1500) {
  const tasks = [];
  if (document.fonts && document.fonts.ready) tasks.push(document.fonts.ready.catch(() => {}));
  for (const img of Array.from(document.images || [])) {
    if (img.complete) continue;
    tasks.push(
      new Promise((res) => {
        img.addEventListener('load', res, { once: true });
        img.addEventListener('error', res, { once: true });
      })
    );
  }
  await Promise.race([
    Promise.all(tasks),
    new Promise((res) => setTimeout(res, timeoutMs)),
  ]);
}

// Detecte une capture ratee : image illisible ou quasi entierement noire.
function isBlankCapture(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl) { resolve(true); return; }
    const img = new Image();
    img.onload = () => {
      try {
        const s = 24; // echantillon reduit, suffisant pour detecter le noir uniforme
        const c = document.createElement('canvas');
        c.width = s;
        c.height = s;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, s, s);
        const { data } = ctx.getImageData(0, 0, s, s);
        let dark = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] < 12 && data[i + 1] < 12 && data[i + 2] < 12) dark++;
        }
        resolve(dark / (s * s) > 0.985); // >98.5% de pixels quasi-noirs = ratee
      } catch {
        resolve(false); // en cas de doute (canvas tainted...), ne pas rejeter
      }
    };
    img.onerror = () => resolve(true);
    img.src = dataUrl;
  });
}

function captureOnce() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  // On dimensionne la sortie au viewport et on decale le contenu selon le scroll.
  return domToJpeg(document.body, {
    width: w,
    height: h,
    backgroundColor: '#ffffff',
    quality: 0.92,
    style: {
      transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)`,
      transformOrigin: 'top left',
    },
    filter: (node) =>
      !(node instanceof HTMLElement && node.hasAttribute('data-report-ui')),
  });
}

export function useScreenCapture() {
  // Renvoie un dataURL JPEG du viewport visible, ou '' si la capture a echoue.
  async function captureViewport() {
    await waitForResources();

    let raw = '';
    try {
      raw = await captureOnce();
    } catch (e) {
      console.warn('[capture] echec domToJpeg (essai 1)', e);
    }

    // Reessaie une fois si la capture est vide ou noire.
    if (await isBlankCapture(raw)) {
      await nextFrames();
      try {
        raw = await captureOnce();
      } catch (e) {
        console.warn('[capture] echec domToJpeg (essai 2)', e);
        raw = '';
      }
      if (await isBlankCapture(raw)) {
        console.warn('[capture] capture abandonnee (noire/vide apres 2 essais)');
        return '';
      }
    }

    // Recompresse / borne la taille (max 1400px de large) pour limiter le poids.
    const blob = await compressImage(raw, { maxWidth: 1400, quality: 0.8 });
    return blobToDataUrl(blob);
  }

  return { captureViewport };
}
